import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Calendar as PRCalendar } from "primereact/calendar";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import {
  CirclePlus, Eye, SquarePen, Upload, Download,
  User, Shield, MapPin, Globe, CreditCard,
  Briefcase, Phone, Mail, CheckCircle2,
  AlertCircle, ChevronLeft, Info, FileText,
  Calendar as CalendarIcon
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { post, get } from "../../utils/api";
import Button from "../Button/Button";
import ReusableTable from "../Table/ReusableTable";
import CustomDialog from "../dialog/CustomDialog";
import JSZip from 'jszip';

// Modern Section Wrapper
const FormSection = ({ title, icon: Icon, children, className = "", action }) => (
  <div className={`bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 ${className}`}>
    <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/30 flex items-center justify-between">
      <div className="flex items-center gap-3">
        {Icon && <Icon size={20} className="text-indigo-500" />}
        <h2 className="text-sm font-bold text-gray-800 uppercase tracking-tight">{title}</h2>
      </div>
      {action && <div>{action}</div>}
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

// Memoized InputField component
const InputField = React.memo(
  ({
    label,
    value,
    onChange,
    readOnly = false,
    className = "",
    isNumeric = false,
    placeholder,
    error,
    icon: Icon
  }) => (
    <div className="flex flex-col gap-1.5 min-w-0">
      <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider ml-1 flex items-center gap-1.5">
        {Icon && <Icon size={12} className="text-indigo-400" />}
        {label}
      </label>
      <div className="relative group">
        <input
          type="text"
          inputMode={isNumeric ? "decimal" : "text"}
          value={value ?? ""}
          onChange={onChange}
          readOnly={readOnly}
          placeholder={placeholder}
          className={`w-full h-[42px] px-4 rounded-lg bg-gray-50/50 border ${error ? "border-red-400 focus:ring-red-100" : "border-gray-100 group-hover:border-indigo-200 focus:border-indigo-500 focus:ring-indigo-100"
            } transition-all outline-none text-sm font-semibold text-gray-700 placeholder:text-gray-300 ${readOnly ? "bg-gray-100 cursor-not-allowed text-gray-500 border-dashed" : "focus:ring-2 focus:bg-white"
            } ${className}`}
          onKeyPress={(e) => {
            if (isNumeric) {
              if (!/[\d.]/.test(e.key) && !["Backspace", "Delete", "Tab", "Enter"].includes(e.key)) {
                e.preventDefault();
              }
              if (e.key === "." && e.target.value.includes(".")) {
                e.preventDefault();
              }
            }
          }}
        />
        {error && <div className="absolute -bottom-5 left-1 text-[10px] font-bold text-red-500 uppercase">{error}</div>}
      </div>
    </div>
  )
);

const AddDistributorForm = ({
  userId,
  distributorData,
  onBack,
  readOnly: propReadOnly = false,
  roleId: propRoleId = null,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  console.log(distributorData, "Distributor Data in Form");


  // Add these states for rejection modal
  const [rejectionModalVisible, setRejectionModalVisible] = useState(false);
  const [rejectionRemark, setRejectionRemark] = useState("");
  const [rejectionType, setRejectionType] = useState(""); // "sales" or "amc"
  const [pendingRejection, setPendingRejection] = useState(false);

  // Add states for E-sign modal
  const [esignModalVisible, setEsignModalVisible] = useState(false);
  const [inPresenceOf, setInPresenceOf] = useState("");
  const [signzyContractId, setSignzyContractId] = useState("");
  const [esignLoading, setEsignLoading] = useState(false);

  const [distributor_id, setDistributorId] = useState(0);
  const [form, setForm] = useState({
    distributor_name: "",
    arn_code: "",
    arn_expiry_date: null,
    pan_number: "",
    pin_code: "",
    city: "",
    address: "",
    is_arn_active: true,
    spoc_name: "",
    spoc_email1: "",
    spoc_email2: "",
    spoc_mobile1: "",
    spoc_mobile2: "",
    spoc_is_primary: false,
    bank_name: "",
    account_holder_name: "",
    account_number: "",
    ifsc_code: "",
    bank_branch: "",
    authorized_signatory: "",
  });
  const [spocList, setSpocList] = useState([]);
  const [editingSpocId, setEditingSpocId] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);
  const inputRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [bankDetailsList, setBankDetailsList] = useState([]);
  const [editingBankId, setEditingBankId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usePreviousDetails, setUsePreviousDetails] = useState(false);
  // True when APRN Code was auto-filled from an API response (locked); false lets the user type it in.
  const [isArnFromApi, setIsArnFromApi] = useState(false);



  useEffect(() => {
    const storedContractId = localStorage.getItem('signzy_contractId');
    if (storedContractId) {
      setSignzyContractId(storedContractId);
    }
  }, []);



  // KYC DOCUMENTS STATE START
  const [kycMenuTypes, setKycMenuTypes] = useState([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [kycDocuments, setKycDocuments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(null);
  // KYC DOCUMENTS STATE END


  const userDetails = JSON.parse(localStorage.getItem("user_details") || "{}");
  const roleId = userDetails?.role_id;
  const userEmail = userDetails?.email;
  const propRole = propRoleId || roleId;

  const isReadOnly =
    propReadOnly === true ||
    (roleId === 2 && distributorData?.onboarding_form_status === 3);

  // =========================================================================
  // NEW: PROXY DOWNLOAD FUNCTION TO HANDLE CORS
  // =========================================================================

  const downloadDocumentThroughProxy = async (filePath, fileName) => {
    try {
      console.log("📥 Downloading through proxy:", { filePath, fileName });

      const response = await post(
        "/onboarding/download-document",
        {
          file_url: filePath,
          file_name: fileName
        },
        {
          responseType: 'blob' // Important for binary data
        }
      );

      console.log("✅ Proxy download response received");
      return response.data;
    } catch (error) {
      console.error("❌ Proxy download error:", error);
      throw new Error(`Failed to download document: ${error.message}`);
    }
  };

  // =========================================================================
  // UPDATED DOWNLOAD HANDLERS WITH PROXY
  // =========================================================================

  // INDIVIDUAL DOCUMENT DOWNLOAD HANDLER - UPDATED
  const handleDownloadDocument = async (documentId, fileName, filePath) => {
    if (!filePath) {
      toast.error("No document available to download");
      return;
    }

    try {
      toast.info("Starting download...");

      // Use proxy for CloudFront URLs to avoid CORS
      const blob = await downloadDocumentThroughProxy(filePath, fileName);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `document-${documentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(url);

      toast.success("Document downloaded successfully");
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Error downloading document: " + error.message);
    }
  };

  // UPDATED BULK DOWNLOAD ALL DOCUMENTS HANDLER
  const handleDownloadAllDocuments = async () => {
    if (!distributor_id) {
      toast.error("No distributor selected");
      return;
    }

    let uploadedDocuments = [];

    // Check both sources for documents
    if (isReadOnly && distributorData?.kyc_documents) {
      uploadedDocuments = distributorData.kyc_documents.filter(doc => doc.file_path);
    } else {
      uploadedDocuments = kycDocuments.filter(doc => doc.file_path);
    }

    if (uploadedDocuments.length === 0) {
      toast.error("No uploaded documents found");
      return;
    }

    try {
      toast.info(`Preparing ${uploadedDocuments.length} documents for download...`);

      const zip = new JSZip();
      let downloadErrors = 0;
      let successfulDownloads = 0;

      // Download each file through proxy and add to ZIP
      for (const doc of uploadedDocuments) {
        try {
          console.log(`📥 Downloading: ${doc.file_name}`);
          const blob = await downloadDocumentThroughProxy(doc.file_path, doc.file_name);
          const fileName = doc.file_name || `document-${doc.document_id || doc.id}.pdf`;
          zip.file(fileName, blob);
          successfulDownloads++;
          console.log(`✅ Downloaded: ${doc.file_name}`);
        } catch (error) {
          console.error(`❌ Error downloading ${doc.file_name}:`, error);
          downloadErrors++;
        }
      }

      if (successfulDownloads === 0) {
        toast.error("Failed to download any documents. Please check your connection and try again.");
        return;
      }

      if (downloadErrors > 0) {
        toast.warn(`Failed to download ${downloadErrors} documents. Continuing with ${successfulDownloads} files.`);
      }

      // Generate ZIP file
      toast.info("Creating ZIP file...");
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: "DEFLATE",
        compressionOptions: { level: 6 }
      });

      // Create download link
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `KYC-Documents-Distributor-${distributor_id}-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up URL object
      URL.revokeObjectURL(link.href);

      toast.success(`Successfully downloaded ${successfulDownloads} documents as ZIP file`);

    } catch (error) {
      console.error("Error creating ZIP file:", error);
      toast.error("Error creating download package. Please try downloading documents individually.");
    }
  };

  // =========================================================================
  // REST OF YOUR EXISTING CODE (UNCHANGED)
  // =========================================================================

  useEffect(() => {
    if (distributorData && distributorData.kyc_documents && distributorData.kyc_documents.length > 0) {

      const menuId = distributorData.kyc_documents[0].menu_id;
      setSelectedDocumentType(menuId);

      // When opening the form in edit mode (e.g., kyc_status = 'Draft'),
      // ensure we load the document templates for the selected menu so
      // `kycDocuments` gets populated and the loader disappears.
      if (menuId) {
        fetchKycDocumentsByMenu(menuId);
      }

      if (distributorData.kyc_menu_types) {
        setKycMenuTypes(distributorData.kyc_menu_types);
      }
    }
  }, [distributorData]);

  useEffect(() => {
    const persistFormData = () => {
      const formDataToPersist = {
        spocList,
        bankDetailsList,

        distributor_sequence_number: distributor_id,
        form: {
          distributor_name: form.distributor_name,
          arn_code: form.arn_code,
          arn_expiry_date: form.arn_expiry_date
            ? form.arn_expiry_date.toISOString()
            : null,
          pan_number: form.pan_number,
          pin_code: form.pin_code,
          city: form.city,
          address: form.address,
          authorized_signatory: form.authorized_signatory,
          is_arn_active: form.is_arn_active,
        },
        timestamp: new Date().getTime(),
      };
      localStorage.setItem(
        "distributor_draft_data",
        JSON.stringify(formDataToPersist)
      );
    };


    if (
      !distributorData &&
      (spocList.length > 0 ||
        bankDetailsList.length > 0 ||

        form.distributor_name ||
        form.arn_expiry_date)
    ) {
      persistFormData();
    }
  }, [
    spocList,
    bankDetailsList,

    distributor_id,
    form,
    distributorData,
  ]);

  // ✅ RESTORE PERSISTED DATA ON COMPONENT MOUNT (DON'T CLEAR IMMEDIATELY)
  useEffect(() => {
    const restorePersistedData = () => {
      try {
        const persistedData = localStorage.getItem("distributor_draft_data");

        if (persistedData) {
          const parsedData = JSON.parse(persistedData);

          console.log("🔍 Restoring persisted data:", parsedData);

          if (!distributorData && (!distributor_id || distributor_id === 0)) {

            if (
              Array.isArray(parsedData.spocList) &&
              parsedData.spocList.length > 0
            ) {
              setSpocList(parsedData.spocList);
              console.log("✅ Restored SPOCs:", parsedData.spocList.length);
            }

            if (
              Array.isArray(parsedData.bankDetailsList) &&
              parsedData.bankDetailsList.length > 0
            ) {
              setBankDetailsList(parsedData.bankDetailsList);
              console.log(
                "✅ Restored Bank details:",
                parsedData.bankDetailsList.length
              );
            }



            // Restore distributor_sequence_number if available
            if (parsedData.distributor_sequence_number && parsedData.distributor_sequence_number > 0) {
              setDistributorId(parsedData.distributor_sequence_number);
            }

            // Restore basic form fields if we have distributor name (indicating this is a valid draft)
            if (parsedData.form) {
              // ✅ Properly handle ARN expiry date restoration
              let restoredExpiryDate = null;
              if (parsedData.form.arn_expiry_date) {
                try {
                  restoredExpiryDate = new Date(
                    parsedData.form.arn_expiry_date
                  );
                  // Check if the date is valid
                  if (isNaN(restoredExpiryDate.getTime())) {
                    console.warn(
                      "Invalid ARN expiry date found in localStorage:",
                      parsedData.form.arn_expiry_date
                    );
                    restoredExpiryDate = null;
                  }
                } catch (error) {
                  console.error("Error parsing ARN expiry date:", error);
                  restoredExpiryDate = null;
                }
              }

              setForm((prev) => ({
                ...prev,
                distributor_name:
                  parsedData.form.distributor_name || prev.distributor_name,
                arn_code: parsedData.form.arn_code || prev.arn_code,
                arn_expiry_date: restoredExpiryDate,
                pan_number: parsedData.form.pan_number || prev.pan_number,
                pin_code: parsedData.form.pin_code || prev.pin_code,
                city: parsedData.form.city || prev.city,
                address: parsedData.form.address || prev.address,
                authorized_signatory:
                  parsedData.form.authorized_signatory ||
                  prev.authorized_signatory,
                is_arn_active:
                  parsedData.form.is_arn_active !== undefined
                    ? parsedData.form.is_arn_active
                    : prev.is_arn_active,
              }));
            }

            toast.info("Draft data restored from previous session");
          }
        }
      } catch (error) {
        console.error("❌ Error restoring persisted data:", error);
        // Clear corrupted data
        localStorage.removeItem("distributor_draft_data");
      }
    };

    // Only attempt restoration if we're not in edit mode
    if (!distributorData) {
      restorePersistedData();
    }
  }, [distributorData]);

  // ✅ AUTO-FILL DISTRIBUTOR DETAILS FROM LOGGED-IN USER EMAIL
  useEffect(() => {
    const autoFillDistributorDetails = async () => {
      if (roleId === 2 && userEmail && !distributorData) {
        setLoading(true);
        try {
          console.log(
            "🔍 Auto-filling distributor details for email:",
            userEmail
          );

          const response = await get(
            `/onboarding/getDistributorByEmail?email=${encodeURIComponent(
              userEmail
            )}`
          );

          if (response.data?.Success && response.data.Data) {
            const distributor = response.data.Data;
            console.log("✅ Found distributor details:", distributor);

            setForm((prev) => ({
              ...prev,
              distributor_name: distributor.distributor_name || "",
              arn_code: distributor.arn_code ? distributor.arn_code.toUpperCase() : "",
              pan_number: distributor.pan_number || "",
              pin_code: distributor.pincode || "",
              city: distributor.city || "",
              address: distributor.address || "",
            }));

            // If the API already has an APRN Code, lock it; otherwise let the user fill it in.
            setIsArnFromApi(Boolean(distributor.arn_code));

            if (distributor.distributor_sequence_number) {
              setDistributorId(distributor.distributor_sequence_number);
            }

            toast.success("Distributor details auto-filled from your account");
          } else {
            console.log("❌ No distributor found for email:", userEmail);
            toast.info(
              "No distributor details found for your email. Please fill the form manually."
            );
          }
        } catch (error) {
          console.error("❌ Error fetching distributor details:", error);
          toast.error(
            "Failed to load distributor details. Please fill the form manually."
          );
        } finally {
          setLoading(false);
        }
      }
    };

    autoFillDistributorDetails();
  }, [userEmail, roleId, distributorData]);

  // ✅ FALLBACK: Auto-fill from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlDistributorName = searchParams.get("distributor_name");
    const urlArnCode = searchParams.get("arn_code");
    const urlDistributorId = searchParams.get("distributor_id");

    if (
      urlDistributorName &&
      urlArnCode &&
      !form.distributor_name &&
      !form.arn_code &&
      !distributorData
    ) {
      console.log("🔗 Auto-filling form from URL parameters:", {
        distributor_name: urlDistributorName,
        arn_code: urlArnCode,
        distributor_id: urlDistributorId,
      });

      setForm((prev) => ({
        ...prev,
        distributor_name: decodeURIComponent(urlDistributorName),
        arn_code: decodeURIComponent(urlArnCode),
      }));

      setIsArnFromApi(Boolean(urlArnCode));

      if (urlDistributorId) {
        setDistributorId(parseInt(urlDistributorId));
      }

      toast.success("Distributor details auto-filled from invitation link");
    }
  }, [location.search, form.distributor_name, form.arn_code, distributorData]);

  // ✅ FIXED: Enhanced data processing for all fund types
  useEffect(() => {
    if (distributorData) {
      console.log("📥 Processing distributorData:", distributorData);

      setDistributorId(distributorData.distributor_sequence_number || 0);

      // Set basic form data
      const expiryDate = distributorData.arn_expiry_date
        ? moment(distributorData.arn_expiry_date).isValid()
          ? moment(distributorData.arn_expiry_date).toDate()
          : null
        : null;

      setForm((prev) => ({
        ...prev,
        distributor_name: distributorData.distributor_name || "",
        arn_code: distributorData.arn_code || "",
        arn_expiry_date: expiryDate,
        pan_number: distributorData.pan_number || "",
        pin_code: distributorData.pincode || distributorData.pin_code || "",
        city: distributorData.city || "",
        address: distributorData.address || "",
        authorized_signatory: distributorData.authorized_signatory || "",
        is_arn_active: distributorData.is_arn_active ?? true,
      }));

      // If the existing record already has an APRN Code, lock it; otherwise let the user fill it in.
      setIsArnFromApi(Boolean(distributorData.arn_code));

      // Set SPOC data
      if (Array.isArray(distributorData.spoc_details)) {
        const mappedSpocs = distributorData.spoc_details.map((s) => ({
          spoc_id: s.id,
          spoc_name: s.spoc_name,
          spoc_email1: s.email_1,
          spoc_email2: s.email_2,
          spoc_mobile1: s.mobile_1,
          spoc_mobile2: s.mobile_2,
          spoc_is_primary: s.is_primary ? "Yes" : "No",
        }));
        setSpocList(mappedSpocs);
      }

      // Set Bank data
      if (Array.isArray(distributorData.bank_details)) {
        setBankDetailsList(distributorData.bank_details);
      }

      // Process fund data
      // processFundData(distributorData);
    }
  }, [distributorData]);



  // Check if ARN is expired based on expiry date
  const isArnExpired = () => {
    if (!form.arn_expiry_date) return false;
    const expiryDate = new Date(form.arn_expiry_date);
    const today = new Date();
    return expiryDate.setHours(0, 0, 0, 0) < today.setHours(0, 0, 0, 0);
  };

  // Check if at least one SPOC is marked as primary
  const hasPrimarySpoc = () => {
    return spocList.some((spoc) => spoc.spoc_is_primary === "Yes");
  };

  const handleSaveDraft = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    // Prepare minimal payload for draft save (no strict validation)
    const payload = {
      action: "draft",
      distributor_sequence_number: distributor_id || undefined,
      distributor_name: form.distributor_name,
      arn_code: form.arn_code,
      arn_expiry_date: form.arn_expiry_date
        ? new Date(form.arn_expiry_date).toISOString().split("T")[0]
        : null,
      pan_number: form.pan_number,
      distributor_email: form.distributor_email,
      city: form.city,
      address: form.address,
      pincode: form.pin_code,
      authorized_signatory: form.authorized_signatory,
      is_arn_active: form.is_arn_active ?? true,

      spoc_details: spocList.map((spoc) => ({
        spoc_name: spoc.spoc_name,
        email_1: spoc.spoc_email1,
        email_2: spoc.spoc_email2 || "",
        mobile_1: spoc.spoc_mobile1,
        mobile_2: spoc.spoc_mobile2 || "",
        is_primary: spoc.spoc_is_primary,
      })),

      bank_details: bankDetailsList.map((bank) => ({
        bank_name: bank.bank_name,
        account_holder_name: bank.account_holder_name,
        account_number: bank.account_number,
        ifsc_code: bank.ifsc_code,
        bank_branch: bank.bank_branch,
      })),
    };

    try {
      setLoading(true);
      const resp = await post("/onboarding/saveOnboardingForm", payload);

      if (resp?.data?.Success) {
        // Update distributor id from response so future saves update the same record
        // Update distributor id from response so future saves update the same record
        let savedId = resp.data?.Data?.distributor_sequence_number;

        if (!savedId && resp.data?.Data && typeof resp.data.Data !== 'object') {
          savedId = resp.data.Data;
        }

        if (savedId) {
          setDistributorId(savedId);
        }

        // Clear client-side persisted draft to avoid conflicts
        localStorage.removeItem("distributor_draft_data");

        toast.success(resp.data.Message || "Draft saved");
      } else {
        toast.error(resp?.data?.Message || "Failed to save draft. Please try again.");
      }
    } catch (error) {
      console.error("❌ Error saving draft:", error);
      toast.error("Failed to save draft. Please try again.");
    } finally {
      setLoading(false);
    }
  };



  // Re-implementing handleSubmit safely
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Existing validations remain the same
    if (!form.distributor_name?.trim()) {
      toast.error("Distributor name is required");
      return;
    }

    // if (!form.arn_code?.trim()) {
    //   toast.error("ARN code is required");
    //   return;
    // }

    // if (!form.arn_expiry_date) {
    //   toast.error("ARN expiry date is required");
    //   return;
    // }

    // Check if ARN is expired
    if (isArnExpired()) {
      toast.error("APRN is expired. Please update expiry date.");
      return;
    }

    if (!hasPrimarySpoc()) {
      toast.error("At least one SPOC must be marked as primary");
      return;
    }

    // ✅ FIXED: KYC Document Validation - Check only MANDATORY documents for selected menu
    if (distributor_id && distributor_id > 0 && selectedDocumentType) {
      try {
        console.log(
          "🔍 Validating MANDATORY KYC documents for menu:",
          selectedDocumentType
        );

        // Fetch uploaded KYC documents for this distributor
        const kycResponse = await get(
          `/onboarding/getUploadedKYCDocuments/${distributor_id}`
        );

        if (kycResponse.data.Success) {
          const uploadedDocs = kycResponse.data.Data || [];

          // Get the selected document type name for error message
          const selectedMenu = kycMenuTypes.find(
            (menu) => menu.menu_id == selectedDocumentType
          );
          const menuName = selectedMenu
            ? selectedMenu.menu_name
            : "selected document type";

          // Fetch the required documents for the selected menu type
          const docsResponse = await get(
            `/onboarding/getKYCDocumentsByMenu/${selectedDocumentType}`
          );

          if (docsResponse.data.Success) {
            const allDocuments = docsResponse.data.Data || [];

            // Filter only MANDATORY documents (is_mandatory = true)
            const mandatoryDocuments = allDocuments.filter(
              (doc) => doc.is_mandatory === true
            );

            console.log(
              `📋 Found ${mandatoryDocuments.length} mandatory documents for ${menuName}`
            );

            // Check if ALL MANDATORY documents are uploaded
            const missingMandatoryDocuments = mandatoryDocuments.filter(
              (mandatoryDoc) => {
                const isUploaded = uploadedDocs.some(
                  (uploadedDoc) =>
                    uploadedDoc.document_id == mandatoryDoc.document_id &&
                    uploadedDoc.menu_id == selectedDocumentType
                );
                return !isUploaded;
              }
            );

            if (missingMandatoryDocuments.length > 0) {
              console.log(
                `❌ Missing ${missingMandatoryDocuments.length} mandatory documents`
              );
              toast.error(
                `Please upload all mandatory documents for ${menuName} before submitting`
              );
              return;
            }

            console.log(
              "✅ KYC document validation passed - all mandatory documents uploaded"
            );
          } else {
            console.error("Failed to fetch required KYC documents");
            toast.error(
              "Unable to validate required documents. Please try again."
            );
            return;
          }
        } else {
          console.error("Failed to fetch uploaded KYC documents");
          toast.error("Unable to validate KYC documents. Please try again.");
          return;
        }
      } catch (error) {
        console.error("❌ Error during KYC document validation:", error);
        toast.error("Error validating KYC documents. Please try again.");
        return;
      }
    } else if (distributor_id && distributor_id > 0 && !selectedDocumentType) {
      // If distributor exists but no document type is selected
      toast.error(
        "Please select a document type and upload all mandatory KYC documents before submitting"
      );
      return;
    } else if (!distributor_id) {
      // If no distributor_id, KYC documents can't be uploaded yet, so we can proceed
      console.log("ℹ️  No distributor ID yet, skipping KYC validation");
    }


    // Continue with existing submission logic...
    const payload = {
      action: "submit",
      distributor_name: form.distributor_name,
      arn_code: form.arn_code,
      arn_expiry_date: form.arn_expiry_date
        ? new Date(form.arn_expiry_date).toISOString().split("T")[0]
        : null,
      pan_number: form.pan_number,
      distributor_email: form.distributor_email,
      city: form.city,
      address: form.address,
      pincode: form.pin_code,
      authorized_signatory: form.authorized_signatory,
      is_arn_active: form.is_arn_active ?? true,

      spoc_details: spocList.map((spoc) => ({
        spoc_name: spoc.spoc_name,
        email_1: spoc.spoc_email1,
        email_2: spoc.spoc_email2 || "",
        mobile_1: spoc.spoc_mobile1,
        mobile_2: spoc.spoc_mobile2 || "",
        is_primary: spoc.spoc_is_primary,
      })),

      bank_details: bankDetailsList.map((bank) => ({
        bank_name: bank.bank_name,
        account_holder_name: bank.account_holder_name,
        account_number: bank.account_number,
        ifsc_code: bank.ifsc_code,
        bank_branch: bank.bank_branch,
      })),


    };

    try {
      setLoading(true); // Disable button
      const resp = await post("/onboarding/saveOnboardingForm", payload);

      if (resp?.data?.Success) {
        // ✅ CLEAR LOCALSTORAGE ONLY ON SUCCESSFUL SUBMISSION
        localStorage.removeItem("distributor_draft_data");
        console.log("🗑️ LocalStorage cleared after successful form submission");

        toast.success(resp.data.Message || "Form submitted successfully!");

        // Navigation logic
        if (onBack) {
          onBack();
        } else if (navigate) {
          navigate("/onboarding_listing");
        } else {
          // Fallback for standalone/no-hook scenario (should not happen with proper hook usage)
          window.location.href = "/onboarding_listing";
        }

      } else {
        toast.error(
          resp?.data?.Message || "Failed to submit form. Please try again."
        );
        setLoading(false); // Enable button if failed (important!)
      }
    } catch (error) {
      console.error("❌ Error submitting form:", error);
      toast.error("Failed to submit form. Please try again.");
      setLoading(false); // Enable button if error
    }
  };



  // ✅ FIXED: Use useCallback to prevent unnecessary re-renders and focus loss







  // Update is_arn_active based on expiry date
  useEffect(() => {
    const expired = isArnExpired();
    if (expired && form.is_arn_active) {
      setForm((prev) => ({ ...prev, is_arn_active: false }));
    }
  }, [form.arn_expiry_date]);

  useEffect(() => {
    if (distributorData && distributorData.distributor_sequence_number) {
      setDistributorId(distributorData.distributor_sequence_number);

      const expiryDate = distributorData.arn_expiry_date
        ? moment(distributorData.arn_expiry_date).isValid()
          ? moment(distributorData.arn_expiry_date).toDate()
          : null
        : null;

      const expired =
        expiryDate &&
        expiryDate.setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);

      setForm((prev) => ({
        ...prev,
        distributor_name: distributorData.distributor_name || "",
        arn_code: distributorData.arn_code || "",
        arn_expiry_date: expiryDate,
        pan_number: distributorData.pan_number || "",
        pin_code: distributorData.pincode || distributorData.pin_code || "",
        city: distributorData.city || "",
        address: distributorData.address || "",
        is_arn_active: expired
          ? false
          : distributorData.is_arn_active ?? prev.is_arn_active ?? true,
        authorized_signatory:
          distributorData.authorized_signatory ||
          prev.authorized_signatory ||
          "",
        spoc_name: "",
        spoc_email1: "",
        spoc_email2: "",
        spoc_mobile1: "",
        spoc_mobile2: "",
        spoc_is_primary: false,
      }));

      if (
        Array.isArray(distributorData.spoc_details) &&
        distributorData.spoc_details.length > 0
      ) {
        const mapped = distributorData.spoc_details.map((s) => ({
          spoc_id: s.id,
          spoc_name: s.spoc_name,
          spoc_email1: s.email_1,
          spoc_email2: s.email_2,
          spoc_mobile1: s.mobile_1,
          spoc_mobile2: s.mobile_2,
          spoc_is_primary: s.is_primary ? "Yes" : "No",
        }));
        setSpocList(mapped);
      } else {
        fetchSpocsFromAPI(distributorData.distributor_sequence_number);
      }

      if (
        Array.isArray(distributorData.bank_details) &&
        distributorData.bank_details.length > 0
      ) {
        setBankDetailsList(distributorData.bank_details);
      } else {
        fetchBankDetails(distributorData.distributor_sequence_number);
      }


    }
  }, [distributorData]);

  useEffect(() => {
    const handler = (e) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setIsSaveDisabled(!isFormValid());
  }, [form, distributor_id, spocList]);

  const handleChange = (field, value) => {
    if (field === "pan_number") {
      value = value.toUpperCase();
    }
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validateFields = () => {
    const errs = {};

    const requiredFields = [
      "distributor_name",
      "pan_number",
      "pin_code",
      "city",
      "address",
      "spoc_name",
      "spoc_email1",
      "spoc_mobile1",
    ];
    requiredFields.forEach((field) => {
      if (!form[field]) {
        errs[field] = "Required";
      }
    });

    if (form.arn_code && !form.arn_expiry_date) {
      errs.arn_expiry_date = "Required when APRN code is provided";
    }

    if (!distributor_id) {
      if (!form.spoc_name) errs.spoc_name = "Required";
      if (!form.spoc_email1) errs.spoc_email1 = "Required";
      if (!form.spoc_mobile1) errs.spoc_mobile1 = "Required";
    }

    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (form.pan_number && !panRegex.test(form.pan_number.toUpperCase())) {
      errs.pan_number = "Invalid PAN format (e.g. ABCDE1234F)";
    }

    const pinRegex = /^[0-9]{6}$/;
    if (form.pin_code && !pinRegex.test(form.pin_code)) {
      errs.pin_code = "Invalid pin code (6 digits)";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    if (form.spoc_email1 && !emailRegex.test(form.spoc_email1)) {
      errs.spoc_email1 = "Invalid email format";
    }
    if (
      form.spoc_email2 &&
      form.spoc_email2.length > 0 &&
      !emailRegex.test(form.spoc_email2)
    ) {
      errs.spoc_email2 = "Invalid email format";
    }

    if (
      form.spoc_email1 &&
      form.spoc_email2 &&
      form.spoc_email1.toLowerCase() === form.spoc_email2.toLowerCase()
    ) {
      errs.spoc_email2 = "Email 2 must be different from Email 1";
    }

    const mobileRegex = /^[6-9][0-9]{9}$/;
    if (form.spoc_mobile1 && !mobileRegex.test(form.spoc_mobile1)) {
      errs.spoc_mobile1 =
        "Invalid mobile number (must start with 6-9 and be 10 digits)";
    }
    if (
      form.spoc_mobile2 &&
      form.spoc_mobile2.length > 0 &&
      !mobileRegex.test(form.spoc_mobile2)
    ) {
      errs.spoc_mobile2 =
        "Invalid mobile number (must start with 6-9 and be 10 digits)";
    }

    if (
      form.spoc_mobile1 &&
      form.spoc_mobile2 &&
      form.spoc_mobile1 === form.spoc_mobile2
    ) {
      errs.spoc_mobile2 = "Mobile 2 must be different from Mobile 1";
    }

    if (!hasPrimarySpoc() && !form.spoc_is_primary) {
      errs.spoc_is_primary = "At least one SPOC must be marked as primary";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const fetchSpocsFromAPI = async (id) => {
    try {
      const resp = await post("/distributor/fetchDistributorsSPOC", {
        distributor_sequence_number: id,
      });
      if (resp.data.Success && Array.isArray(resp.data.Data)) {
        const mapped = resp.data.Data.map((s) => ({
          spoc_id: s.id,
          spoc_name: s.spoc_name,
          spoc_email1: s.email_1,
          spoc_email2: s.email_2,
          spoc_mobile1: s.mobile_1,
          spoc_mobile2: s.mobile_2,
          spoc_is_primary: s.is_primary ? "Yes" : "No",
        }));
        setSpocList(mapped);
      } else {
        setSpocList([]);
        toast.warn("No SPOC records found.");
      }
    } catch {
      toast.error("Failed to fetch SPOCs.");
    }
  };

  const isFormValid = () => {
    const {
      distributor_name,
      arn_code,
      arn_expiry_date,
      pan_number,
      pin_code,
      city,
      address,
      spoc_name,
      spoc_email1,
      spoc_mobile1,
    } = form;

    if (!distributor_name || !pan_number || !pin_code || !city || !address) {
      return false;
    }

    if (arn_code && !arn_expiry_date) {
      return false;
    }

    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(pan_number)) return false;

    const pinRegex = /^[0-9]{6}$/;
    if (!pinRegex.test(pin_code)) return false;

    if (!hasPrimarySpoc() && !form.spoc_is_primary) {
      return false;
    }

    if (spoc_name || spoc_email1 || spoc_mobile1) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      const mobileRegex = /^[6-9][0-9]{9}$/;

      if (!spoc_name || !spoc_email1 || !spoc_mobile1) return false;
      if (!emailRegex.test(spoc_email1)) return false;
      if (!mobileRegex.test(spoc_mobile1)) return false;
    }

    return true;
  };

  const handleAddSpoc = async () => {
    console.log("Adding SPOC with form data:", form);

    if (isReadOnly) {
      toast.warn("Form is read-only. Action not allowed.");
      return;
    }
    const requiredSpocFields = {
      spoc_name: "SPOC Name",
      spoc_email1: "Email 1",
      spoc_mobile1: "Mobile 1",
    };

    const emptyFields = Object.entries(requiredSpocFields)
      .filter(([field]) => !form[field]?.trim())
      .map(([_, label]) => label);

    if (emptyFields.length > 0) {
      toast.error(
        `Please fill required SPOC fields: ${emptyFields.join(", ")}`
      );

      const newErrors = {};
      Object.keys(requiredSpocFields).forEach((field) => {
        if (!form[field]?.trim()) {
          newErrors[field] = "Required";
        }
      });
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return;
    }

    // RELAXED VALIDATION: Only check core fields needed for SPOC addition
    const spocErrs = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    const mobileRegex = /^[6-9][0-9]{9}$/;

    if (!emailRegex.test(form.spoc_email1)) spocErrs.spoc_email1 = "Invalid email format";
    if (!mobileRegex.test(form.spoc_mobile1)) spocErrs.spoc_mobile1 = "Invalid mobile (10 digits starting 6-9)";

    if (Object.keys(spocErrs).length > 0) {
      setErrors(prev => ({ ...prev, ...spocErrs }));
      toast.error("Please correct SPOC field errors.");
      return;
    }


    const email = form.spoc_email1.trim().toLowerCase();
    const mobile = form.spoc_mobile1.trim();

    const hasDuplicateInList = spocList.some(
      (spoc) =>
        spoc.spoc_email1.trim().toLowerCase() === email ||
        spoc.spoc_mobile1.trim() === mobile
    );

    if (hasDuplicateInList) {
      toast.error("Duplicate SPOC email or mobile already exists in list.");
      setErrors((prev) => ({
        ...prev,
        spoc_email1: "Duplicate email in list",
        spoc_mobile1: "Duplicate mobile in list",
      }));
      return;
    }

    if (!distributor_id) {
      const hasDuplicateDistributor = await checkDuplicateDistributor();
      if (hasDuplicateDistributor) return;

      const hasDuplicateSpoc = await checkDuplicateSpoc();
      if (hasDuplicateSpoc) return;
    }

    const expired = isArnExpired();

    const formattedExpiryDate = form.arn_expiry_date
      ? form.arn_expiry_date.toISOString().split("T")[0]
      : null;

    try {
      const resp = await post("/distributor/addSPOCDistributor", {
        user_Id: userId,
        distributor_sequence_number: distributor_id || 0,
        distributor_name: (form.distributor_name || "").toUpperCase(),
        arn_code: (form.arn_code || "").toUpperCase(),
        arn_expiry_date: formattedExpiryDate,
        pan_number: (form.pan_number || "").toUpperCase(),
        city: (form.city || "").toUpperCase(),
        address: (form.address || "").toUpperCase(),
        pincode: (form.pin_code || "").toUpperCase(),
        is_arn_active: form.is_arn_active,
        is_active: !expired,
        spoc_details: {
          spoc_name: form.spoc_name,
          email_1: (form.spoc_email1 || "").toUpperCase(),
          email_2: form.spoc_email2 ? (form.spoc_email2 || "").toUpperCase() : null,
          mobile_1: form.spoc_mobile1,
          mobile_2: form.spoc_mobile2,
          is_primary: !!form.spoc_is_primary,
          is_active: true,
        },
      });

      if (resp.data.Success) {
        toast.success("SPOC created successfully");
        let newId = distributor_id;

        // Prioritize sequence number from response if available
        if (resp.data.Data && typeof resp.data.Data === 'object' && resp.data.Data.distributor_sequence_number) {
          newId = resp.data.Data.distributor_sequence_number;
        } else if (!newId && resp.data.Data) {
          newId = resp.data.Data;
        }

        setDistributorId(newId);
        await fetchSpocsFromAPI(newId);

        setForm((prev) => ({
          ...prev,
          spoc_name: "",
          spoc_email1: "",
          spoc_email2: "",
          spoc_mobile1: "",
          spoc_mobile2: "",
          spoc_is_primary: false,
        }));
        setEditingSpocId(null);
      } else {
        toast.error(resp.data.Message || "Add SPOC failed");
      }
    } catch {
      toast.error("Error occurred while adding SPOC");
    }
  };

  const handleUpdateSpoc = async () => {
    if (isReadOnly) {
      toast.warn("Form is read-only. Action not allowed.");
      return;
    }
    // RELAXED VALIDATION for update
    const spocErrs = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    const mobileRegex = /^[6-9][0-9]{9}$/;

    if (!emailRegex.test(form.spoc_email1)) spocErrs.spoc_email1 = "Invalid email format";
    if (!mobileRegex.test(form.spoc_mobile1)) spocErrs.spoc_mobile1 = "Invalid mobile";

    if (Object.keys(spocErrs).length > 0) {
      setErrors(prev => ({ ...prev, ...spocErrs }));
      toast.error("Please correct SPOC field errors.");
      return;
    }

    const email = form.spoc_email1.trim().toLowerCase();
    const mobile = form.spoc_mobile1.trim();

    const hasDuplicateInList = spocList.some(
      (spoc) =>
        spoc.spoc_id !== editingSpocId &&
        (spoc.spoc_email1.trim().toLowerCase() === email ||
          spoc.spoc_mobile1.trim() === mobile)
    );

    if (hasDuplicateInList) {
      toast.error("Duplicate SPOC email or mobile already exists in list.");
      setErrors((prev) => ({
        ...prev,
        spoc_email1: "Duplicate email in list",
        spoc_mobile1: "Duplicate mobile in list",
      }));
      return;
    }

    const hasDuplicateSpoc = await checkDuplicateSpoc();
    if (hasDuplicateSpoc) return;

    try {
      const resp = await post("/distributor/updateSPOC", {
        user_Id: userId,
        distributor_sequence_number: distributor_id,
        spoc_details: {
          spoc_id: editingSpocId,
          spoc_name: form.spoc_name,
          email_1: (form.spoc_email1 || "").toUpperCase(),
          email_2: form.spoc_email2 ? (form.spoc_email2 || "").toUpperCase() : null,
          mobile_1: form.spoc_mobile1,
          mobile_2: form.spoc_mobile2,
          is_primary: !!form.spoc_is_primary,
        },
      });
      if (resp.data.Success) {
        toast.success("SPOC updated successfully");
        await fetchSpocsFromAPI(distributor_id);
        setEditingSpocId(null);
        setForm((prev) => ({
          ...prev,
          spoc_name: "",
          spoc_email1: "",
          spoc_email2: "",
          spoc_mobile1: "",
          spoc_mobile2: "",
          spoc_is_primary: false,
        }));
      } else {
        toast.error(resp.data.Message || "Update SPOC failed");
      }
    } catch {
      toast.error("Error occurred while updating SPOC");
    }
  };

  const checkDuplicateDistributor = async () => {
    try {
      const resp = await post("/distributor/checkDuplicate", {
        arn_code: form.arn_code,
        pan_number: form.pan_number,
        distributor_sequence_number: distributor_id,
      });

      if (resp.data.duplicate) {
        if (resp.data.duplicateField === "arn_code") {
          setErrors((prev) => ({
            ...prev,
            arn_code: "APRN Code already exists",
          }));
          toast.error("APRN Code already exists");
          return true;
        }
        if (resp.data.duplicateField === "pan_number") {
          setErrors((prev) => ({
            ...prev,
            pan_number: "PAN Number already exists",
          }));
          toast.error("PAN Number already exists");
          return true;
        }
      }
      return false;
    } catch (error) {
      toast.error("Error checking for duplicates");
      return true;
    }
  };

  const checkDuplicateSpoc = async () => {
    try {
      const resp = await post("/distributor/checkDuplicateSpoc", {
        email: form.spoc_email1,
        mobile: form.spoc_mobile1,
        distributor_sequence_number: distributor_id,
        spoc_id: editingSpocId,
      });

      if (resp.data.duplicate) {
        if (resp.data.duplicateField === "email") {
          setErrors((prev) => ({
            ...prev,
            spoc_email1: "Email already exists",
          }));
          toast.error("SPOC Email already exists");
          return true;
        }
        if (resp.data.duplicateField === "mobile") {
          setErrors((prev) => ({
            ...prev,
            spoc_mobile1: "Mobile number already exists",
          }));
          toast.error("SPOC Mobile number already exists");
          return true;
        }
      }
      return false;
    } catch (error) {
      toast.error("Error checking for duplicate SPOC");
      return true;
    }
  };

  const toggleArnStatus = async () => {
    if (isArnExpired()) {
      toast.warn(
        "Cannot activate APRN with expired date. Please update expiry date first."
      );
      return;
    }

    if (!distributor_id) {
      toast.warn("Please save the distributor first before toggling status.");
      return;
    }

    try {
      const endpoint = form.is_arn_active
        ? "/distributor/inactivateDistributor"
        : "/distributor/activateDistributor";

      const response = await post(endpoint, {
        distributor_sequence_number: distributor_id,
        user_Id: userId,
      });

      if (response.data?.Success) {
        setForm((prev) => ({ ...prev, is_arn_active: !form.is_arn_active }));
        toast.success(
          `Distributor ${form.is_arn_active ? "inactivated" : "activated"
          } successfully`
        );
      } else {
        toast.error(
          response.data?.Message ||
          `Failed to ${form.is_arn_active ? "inactivate" : "activate"
          } distributor`
        );
      }
    } catch (error) {
      toast.error("An error occurred while updating distributor status");
    }
  };

  const handleSaveDistributor = async () => {
    if (!hasPrimarySpoc()) {
      toast.error("At least one SPOC must be marked as primary before saving.");
      setErrors((prev) => ({
        ...prev,
        spoc_is_primary: "At least one SPOC must be marked as primary",
      }));
      return;
    }

    if (!validateFields()) {
      toast.error("Please fill all required fields correctly.");
      return;
    }

    const expired = isArnExpired();

    try {
      const formattedExpiryDate = form.arn_expiry_date
        ? form.arn_expiry_date.toISOString().split("T")[0]
        : null;

      const resp = await post("/distributor/updateDistributor", {
        distributor_sequence_number: distributor_id,
        user_Id: userId,
        distributor_name: form.distributor_name,
        arn_code: form.arn_code,
        arn_expiry_date: formattedExpiryDate,
        pan_number: form.pan_number,
        city: form.city,
        address: form.address,
        pincode: form.pin_code,
        is_arn_active: expired ? false : form.is_arn_active,
        is_active: !expired,
      });

      if (resp.data.Success) {
        toast.success("Distributor updated successfully");
        setEditingSpocId(null);
        onBack();
      } else {
        toast.error(resp.data.Message || "Update distributor failed");
      }
    } catch (error) {
      toast.error("Error occurred while updating distributor");
    }
  };

  const fetchBankDetails = async (id) => {
    try {
      const resp = await post("/distributor/fetchDistributorBankDetails", {
        distributor_sequence_number: id,
      });
      if (resp.data.Success && Array.isArray(resp.data.Data)) {
        setBankDetailsList(resp.data.Data);
      } else {
        setBankDetailsList([]);
      }
    } catch {
      toast.error("Failed to fetch bank details.");
      setBankDetailsList([]);
    }
  };

  useEffect(() => {
    if (distributor_id && bankDetailsList.length === 0) {
      const hasBankDataFromProps =
        distributorData &&
        Array.isArray(distributorData.bank_details) &&
        distributorData.bank_details.length > 0;

      // if (!hasBankDataFromProps) {
      //   fetchBankDetails(distributor_id);
      // }
    }
  }, [distributor_id]);

  // Agreement funds dropdown removed; no fetch needed





  const handleAddBank = async () => {
    if (isReadOnly) {
      toast.warn("Form is read-only. Action not allowed.");
      return;
    }
    const requiredBankFields = {
      bank_name: "Bank Name",
      account_holder_name: "Account Holder Name",
      account_number: "Account Number",
      ifsc_code: "IFSC Code",
      bank_branch: "Bank Branch",
    };

    const emptyFields = Object.entries(requiredBankFields)
      .filter(([field]) => !form[field]?.trim())
      .map(([_, label]) => label);

    if (emptyFields.length > 0) {
      toast.error(
        `Please fill required bank fields: ${emptyFields.join(", ")}`
      );

      const newErrors = {};
      Object.keys(requiredBankFields).forEach((field) => {
        if (!form[field]?.trim()) {
          newErrors[field] = "Required";
        }
      });
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return;
    }

    const accountNumber = form.account_number.trim();

    const hasDuplicateInList = bankDetailsList.some(
      (bank) => bank.account_number.trim() === accountNumber
    );

    if (hasDuplicateInList) {
      toast.error("Duplicate account number already exists in list.");
      setErrors((prev) => ({
        ...prev,
        account_number: "Duplicate account number in list",
      }));
      return;
    }

    if (!distributor_id) {
      const hasDuplicateDistributor = await checkDuplicateDistributor();
      if (hasDuplicateDistributor) return;

      const hasDuplicateBank = await checkDuplicateBank();
      if (hasDuplicateBank) return;
    }

    const expired = isArnExpired();

    const formattedExpiryDate = form.arn_expiry_date
      ? form.arn_expiry_date.toISOString().split("T")[0]
      : null;

    try {
      const resp = await post("/distributor/addBankDistributor", {
        user_Id: userId,
        distributor_sequence_number: distributor_id,
        distributor_name: form.distributor_name.toUpperCase(),
        arn_code: form.arn_code.toUpperCase(),
        arn_expiry_date: formattedExpiryDate,
        pan_number: form.pan_number.toUpperCase(),
        city: form.city.toUpperCase(),
        address: form.address.toUpperCase(),
        pincode: form.pin_code.toUpperCase(),
        is_arn_active: form.is_arn_active,
        is_active: !expired,
        bank_details: {
          bank_name: form.bank_name,
          account_holder_name: form.account_holder_name,
          account_number: form.account_number,
          ifsc_code: form.ifsc_code,
          bank_branch: form.bank_branch,
          is_active: true,
        },
      });

      if (resp.data.Success) {
        toast.success("Bank details added successfully");
        let newId = distributor_id;

        // Prioritize sequence number from response if available
        if (resp.data.Data && typeof resp.data.Data === 'object' && resp.data.Data.distributor_sequence_number) {
          newId = resp.data.Data.distributor_sequence_number;
        } else if (!newId && resp.data.Data) {
          newId = resp.data.Data;
        }

        setDistributorId(newId);
        await fetchBankDetails(newId);

        setForm((prev) => ({
          ...prev,
          bank_name: "",
          account_holder_name: "",
          account_number: "",
          ifsc_code: "",
          bank_branch: "",
        }));
        setEditingBankId(null);
      } else {
        toast.error(resp.data.Message || "Add Bank failed");
      }
    } catch {
      toast.error("Error occurred while adding Bank Details");
    }
  };

  const handleUpdateBank = async () => {
    if (isReadOnly) {
      toast.warn("Form is read-only. Action not allowed.");
      return;
    }
    const accountNumber = form.account_number.trim();

    const hasDuplicateInList = bankDetailsList.some(
      (bank) =>
        bank.id !== editingBankId &&
        bank.account_number.trim() === accountNumber
    );

    if (hasDuplicateInList) {
      toast.error("Duplicate account number already exists in list.");
      setErrors((prev) => ({
        ...prev,
        account_number: "Duplicate account number in list",
      }));
      return;
    }

    const hasDuplicateBank = await checkDuplicateBank();
    if (hasDuplicateBank) return;

    try {
      const resp = await post("/distributor/updateBank", {
        user_Id: userId,
        distributor_sequence_number: distributor_id,
        bank_details: {
          bank_id: editingBankId,
          bank_name: form.bank_name,
          account_holder_name: form.account_holder_name,
          account_number: form.account_number,
          ifsc_code: form.ifsc_code,
          bank_branch: form.bank_branch,
        },
      });
      if (resp.data.Success) {
        toast.success("Bank details updated successfully");
        await fetchBankDetails(distributor_id);
        setEditingBankId(null);
        setForm((prev) => ({
          ...prev,
          bank_name: "",
          account_holder_name: "",
          account_number: "",
          ifsc_code: "",
          bank_branch: "",
        }));
      } else {
        toast.error(resp.data.Message || "Update Bank failed");
      }
    } catch {
      toast.error("Error occurred while updating bank details");
    }
  };

  const checkDuplicateBank = async () => {
    try {
      const resp = await post("/distributor/checkDuplicateBank", {
        account_number: form.account_number,
        ifsc_code: form.ifsc_code,
        distributor_sequence_number: distributor_id,
        bank_id: editingBankId,
      });

      if (resp.data.duplicate) {
        if (resp.data.duplicateField === "account_number") {
          setErrors((prev) => ({
            ...prev,
            account_number: "Account number already exists",
          }));
          toast.error("Bank Account number already exists");
          return true;
        }
        if (resp.data.duplicateField === "ifsc_code") {
          setErrors((prev) => ({
            ...prev,
            ifsc_code: "IFSC code already exists for this distributor",
          }));
          toast.error("IFSC code already exists for this distributor");
          return true;
        }
      }
      return false;
    } catch (error) {
      toast.error("Error checking for duplicate bank details");
      return true;
    }
  };

  const handleDeleteBank = async (bankId) => {
    if (isReadOnly) {
      toast.warn("Form is read-only. Action not allowed.");
      return;
    }
    try {
      const resp = await post("/distributor/deleteBank", {
        bank_id: bankId,
        user_Id: userId,
      });
      if (resp.data.Success) {
        toast.success("Bank details deleted successfully");
        await fetchBankDetails(distributor_id);
      } else {
        toast.error(resp.data.Message || "Delete failed");
      }
    } catch {
      toast.error("Error deleting bank details");
    }
  };

  const handleDeleteSPOC = async (spocId) => {
    if (isReadOnly) {
      toast.warn("Form is read-only. Action not allowed.");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this SPOC?")) return;
    try {
      const resp = await post("/distributor/deleteSPOC", {
        spoc_id: spocId,
        user_Id: userId,
      });
      if (resp.data.Success) {
        toast.success("SPOC deleted successfully");
        await fetchSpocsFromAPI(distributor_id);
      } else {
        toast.error(resp.data.Message || "Delete failed");
      }
    } catch {
      toast.error("Error deleting SPOC");
    }
  };

  // Rejection Handlers
  const handleFormAmcReject = async () => {
    if (!distributor_id) {
      toast.error("Missing distributor id");
      return;
    }

    // Show modal instead of immediate rejection
    setRejectionType("amc");
    setRejectionModalVisible(true);
    setPendingRejection(false);
  };

  const handleSalesReject = async () => {
    if (!distributor_id) {
      toast.error("Missing distributor id");
      return;
    }

    // Show modal instead of immediate rejection
    setRejectionType("sales");
    setRejectionModalVisible(true);
    setPendingRejection(false);
  };

  // New function to handle AMC rejection with remark
  const handleAmcReject = async () => {
    try {
      const resp = await post("/onboarding/amc/reject", {
        distributor_sequence_number: distributor_id,
        rejection_remark: rejectionRemark.trim(),
      });

      if (resp.data?.Success) {
        toast.success(resp.data.Message || "Rejected by AMC");
        // Close modal and reset states
        setRejectionModalVisible(false);
        setRejectionRemark("");
        setRejectionType("");
        setPendingRejection(false);

        if (onBack) onBack();
      } else {
        toast.error(resp.data?.Message || "Reject failed");
      }
    } catch (err) {
      console.error("Error calling /amc/reject:", err);
      toast.error("Error rejecting. Check console.");
    }
  };

  // New function to handle Sales rejection with remark
  const handleSalesRejectConfirm = async () => {
    try {
      const resp = await post("/onboarding/sales/reject", {
        distributor_sequence_number: distributor_id,
        rejection_remark: rejectionRemark.trim(),
      });

      if (resp.data?.Success) {
        toast.success(resp.data.Message || "Distributor rejected successfully");
        // Close modal and reset states
        setRejectionModalVisible(false);
        setRejectionRemark("");
        setRejectionType("");
        setPendingRejection(false);

        if (onBack) onBack();
      } else {
        toast.error(resp.data?.Message || "Rejection failed");
      }
    } catch (error) {
      console.error("Error rejecting distributor:", error);
      toast.error("Error rejecting distributor");
    }
  };


  const handleEsignSubmit = async () => {
    if (!inPresenceOf.trim()) {
      toast.error("Please enter your name");
      return;
    }

    // Get contractId from localStorage (FIXED)
    const contractId = localStorage.getItem("signzy_contractId");
    if (!contractId) {
      toast.error("Contract ID not found. Please complete distributor E-sign first.");
      return;
    }

    const authorizedSignatory = form.authorized_signatory?.trim();
    if (!authorizedSignatory) {
      toast.error("Authorized signatory information is missing");
      return;
    }

    setEsignLoading(true);
    try {
      // Step 1: Initiate Signzy contract for AMC
      console.log("🔄 Initiating AMC Signzy contract...", {
        name_2: inPresenceOf.trim(), // AMC person name
        contractId: contractId,
        distributor_sequence_number: distributor_id
      });

      // FIXED: Use the correct API endpoint and payload structure
      const signzyResponse = await post("/eSign/initiate-signzy-contract-amc", {
        name_2: inPresenceOf.trim(),       // AMC signer name
        contractId: contractId,           // Existing contract ID from localStorage
        distributor_sequence_number: distributor_id
      });

      console.log("📥 Signzy AMC response:", signzyResponse.data);

      if (signzyResponse.data?.Success) {
        const responseData = signzyResponse.data.Data;

        // ⭐⭐ STORE THE NEW CONTRACT ID FROM AMC API RESPONSE
        const amcContractId = responseData?.contractId;
        console.log("🆕 AMC Contract ID:", amcContractId);

        if (amcContractId) {
          localStorage.setItem("ops_contractId", amcContractId);
          console.log("💾 Stored ops_contractId in localStorage:", amcContractId);
        }

        // FIXED: Extract workflow URL from the response
        let signerUrl = null;

        // Try different possible response structures
        if (responseData?.signerdetail?.[0]?.workflowUrl) {
          signerUrl = responseData.signerdetail[0].workflowUrl;
        } else if (responseData?.workflowUrl) {
          signerUrl = responseData.workflowUrl;
        } else if (responseData?.signerUrl) {
          signerUrl = responseData.signerUrl;
        }

        console.log("🔗 Extracted signer URL:", signerUrl);

        if (!signerUrl) {
          console.error("❌ No workflow URL found in response:", responseData);
          toast.error("Failed to get signing URL from E-sign provider");
          return;
        }

        // FIXED: Open Signzy workflow in new tab
        console.log("🌐 Opening Signzy workflow:", signerUrl);
        window.open(signerUrl, "_blank", "noopener,noreferrer");

        // FIXED: Call AMC approve API
        const amcResponse = await post("/onboarding/amc/approve", {
          distributor_sequence_number: distributor_id,
          signzy_contract_id: contractId,
          esign_initiated: true,
          esign_name: inPresenceOf.trim()
        });

        if (amcResponse.data?.Success) {
          toast.success("✅ Approved by AMC with E-sign initiation");
          setEsignModalVisible(false);
          setInPresenceOf("");

          // Clear localStorage data on successful submission
          localStorage.removeItem("distributor_draft_data");

          if (onBack) onBack();
        } else {
          toast.error(amcResponse.data?.Message || "AMC approval failed after Signzy initiation");
        }
      } else {
        toast.error(signzyResponse.data?.Message || "Failed to initiate E-sign contract");
      }
    } catch (err) {
      console.error("❌ Error in AMC E-sign process:", err);
      if (err.response) {
        console.error("❌ Server response:", err.response.data);
        toast.error(err.response.data?.Message || "Error initiating E-sign process");
      } else {
        toast.error("Error initiating E-sign process. Please try again.");
      }
    } finally {
      setEsignLoading(false);
    }
  };

  // Updated handleFormAmcApprove to show E-sign modal
  // Updated handleFormAmcApprove to show E-sign modal with validation
  const handleFormAmcApprove = async () => {
    if (!distributor_id) {
      toast.error("Missing distributor id");
      return;
    }

    // Check if contract ID exists in localStorage
    const contractId = localStorage.getItem("signzy_contractId");
    if (!contractId) {
      toast.error("Contract ID not found. Distributor must complete E-sign first.");
      return;
    }

    // Check if authorized signatory is filled
    if (!form.authorized_signatory?.trim()) {
      toast.error("Authorized signatory information is required for AMC E-sign");
      return;
    }

    console.log("✅ Pre-check passed, showing E-sign modal");
    console.log("📋 Contract ID:", contractId);
    console.log("📋 Authorized Signatory:", form.authorized_signatory);

    // Show E-sign modal
    setEsignModalVisible(true);
    setSignzyContractId(contractId);
  };

  // =========================================================================
  // KYC DOCUMENTS FUNCTIONS - UPDATED WITH PROXY DOWNLOAD
  // =========================================================================

  useEffect(() => {
    const fetchKycMenuTypes = async () => {
      try {
        const response = await get(`/onboarding/getKYCDocumentTypes`);

        if (response.data.Success) {
          const menuTypes = response.data.Data.map((item) => ({
            menu_id: item.id,
            menu_name: item.document_type,
          }));

          setKycMenuTypes(menuTypes);

          if (distributorData && distributorData.kyc_menu_type) {
            setSelectedDocumentType(distributorData.kyc_menu_type);
            fetchKycDocumentsByMenu(distributorData.kyc_menu_type);
          }
        } else {
          console.error(
            "Failed to fetch KYC menu types:",
            response.data.Message
          );
        }
      } catch (error) {
        console.error("❌ API Error while fetching KYC Menu Types:", error);
      }
    };

    fetchKycMenuTypes();
  }, [distributorData]);

  // Fetch uploaded documents when distributor_id changes
  useEffect(() => {
    if (distributor_id && distributor_id > 0) {
      fetchUploadedKYCDocuments();
    }
  }, [distributor_id]);

  const fetchUploadedKYCDocuments = async () => {
    try {
      console.log(
        `📂 Fetching uploaded documents for distributor: ${distributor_id}`
      );
      const response = await get(
        `/onboarding/getUploadedKYCDocuments/${distributor_id}`
      );

      if (response.data.Success) {
        const uploadedDocs = response.data.Data || [];
        console.log(`✅ Found ${uploadedDocs.length} uploaded documents`);

        // Update kycDocuments with uploaded file information
        setKycDocuments((prev) =>
          prev.map((doc) => {
            const uploadedDoc = uploadedDocs.find(
              (u) => u.document_id === doc.id
            );
            if (uploadedDoc) {
              return {
                ...doc,
                file_name: uploadedDoc.file_name,
                uploaded_date: uploadedDoc.uploaded_date,
                upload_id: uploadedDoc.upload_id,
                file_path: uploadedDoc.file_path,
              };
            }
            return doc;
          })
        );
      } else {
        console.log("No uploaded documents found or API returned failure");
      }
    } catch (error) {
      console.error("❌ Error fetching uploaded KYC documents:", error);
      // Don't show error toast for this - it's expected when no documents exist
    }
  };

  const fetchKycDocumentsByMenu = async (menuId) => {
    try {
      console.log(`📂 Fetching KYC documents for menu: ${menuId}`);
      const response = await get(`/onboarding/getKYCDocumentsByMenu/${menuId}`);

      if (response.data.Success) {
        // Transform API data to match the expected format
        const documents = response.data.Data.map((doc) => ({
          id: doc.document_id,
          document_type: doc.document_name,
          file_name: null,
          uploaded_date: null,
          is_mandatory: doc.is_mandatory,
          mapping_id: doc.mapping_id,
          menu_id: doc.menu_id,
        }));

        setKycDocuments(documents);
        console.log(`✅ Loaded ${documents.length} document templates`);

        // Fetch uploaded documents if we have a distributor_id
        if (distributor_id && distributor_id > 0) {
          fetchUploadedKYCDocuments();
        }
      } else {
        console.error("Failed to fetch KYC documents:", response.data.Message);
        setKycDocuments([]);
      }
    } catch (error) {
      console.error("❌ API Error while fetching KYC Documents:", error);
      setKycDocuments([]);
    }
  };

  const handleDocumentTypeChange = (menuId) => {
    setSelectedDocumentType(menuId);
    if (menuId) {
      fetchKycDocumentsByMenu(menuId);
    } else {
      setKycDocuments([]);
    }
  };

  // ACTUAL FILE UPLOAD HANDLER
  const handleFileUpload = async (event, documentType, documentId) => {
    const file = event.target.files[0];
    if (!file) {
      console.log("❌ No file selected");
      return;
    }

    console.log("📤 File selected for upload:", {
      documentType,
      documentId,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    });

    // Validate file type
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error(
        "Invalid file type. Please upload PDF, JPEG, or PNG files only."
      );
      event.target.value = "";
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File size too large. Maximum size is 5MB.");
      event.target.value = "";
      return;
    }

    if (!distributor_id || distributor_id === 0) {
      toast.error(
        "Please save distributor details before uploading documents."
      );
      event.target.value = "";
      return;
    }

    if (!form.distributor_name) {
      toast.error("Distributor name is required for document upload.");
      event.target.value = "";
      return;
    }

    if (!selectedDocumentType) {
      toast.error("Please select a document type first.");
      event.target.value = "";
      return;
    }

    setUploadingFile(documentType);

    try {
      const formData = new FormData();
      formData.append("document", file);
      formData.append("distributor_sequence_number", distributor_id);
      formData.append("menu_id", selectedDocumentType);
      formData.append("document_id", documentId);
      formData.append("document_type", documentType);
      formData.append("distributor_name", form.distributor_name);

      console.log("📤 Uploading file with data:", {
        distributor_sequence_number: distributor_id,
        menu_id: selectedDocumentType,
        document_id: documentId,
        document_type: documentType,
        distributor_name: form.distributor_name,
      });

      const response = await post("/onboarding/uploadKYCDocument", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("📥 Upload response:", response.data);

      if (response.data.Success) {
        const uploadedDoc = response.data.Data;

        // Update the document in state
        setKycDocuments((prev) =>
          prev.map((doc) =>
            doc.document_type === documentType
              ? {
                ...doc,
                file_name: uploadedDoc.file_name,
                uploaded_date: uploadedDoc.uploaded_date,
                upload_id: uploadedDoc.upload_id,
                file_path: uploadedDoc.file_path,
              }
              : doc
          )
        );

        toast.success(`Document ${documentType} uploaded successfully`);
      } else {
        toast.error(response.data.Message || "Failed to upload document");
      }
    } catch (error) {
      console.error("❌ Error uploading document:", error);
      if (error.response) {
        console.error("❌ Server response:", error.response.data);
        toast.error(
          error.response.data?.Message ||
          "Error uploading document. Please try again."
        );
      } else {
        toast.error("Error uploading document. Please try again.");
      }
    } finally {
      setUploadingFile(null);
      event.target.value = ""; // Reset file input
    }
  };

  // ACTUAL VIEW DOCUMENT HANDLER
  const handleViewDocument = async (documentId, fileName, filePath) => {
    if (!filePath) {
      toast.error("No document available to view");
      return;
    }

    try {
      // Open the document in a new tab
      window.open(filePath, "_blank");
    } catch (error) {
      console.error("Error opening document:", error);
      toast.error("Error opening document");
    }
  };

  // =========================================================================
  // TABLE COLUMNS DEFINITION
  // =========================================================================

  const kycColumns = [
    {
      Header: "Document Type",
      accessor: "document_type",
      size: 200,
      Cell: ({ value, row }) => (
        <div className="px-2 py-1">
          {value}
          {row.original.is_mandatory && (
            <span className="text-red-600 ml-1">*</span>
          )}
        </div>
      ),
    },
    {
      Header: "File Name",
      accessor: "file_name",
      size: 250,
      Cell: ({ value }) => (
        <div className="px-2 py-1 truncate" title={value}>
          {value || "Not Uploaded"}
        </div>
      ),
    },
    {
      Header: "Uploaded Date",
      accessor: "uploaded_date",
      size: 150,
      Cell: ({ value }) => (
        <div className="px-2 py-1">
          {value ? new Date(value).toLocaleDateString() : "-"}
        </div>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      size: 300,
      Cell: ({ row }) => {
        const document = row.original;
        const isDistributor = roleId === 2;
        const fileInputRef = useRef(null);

        const handleUploadClick = () => {
          if (fileInputRef.current) {
            fileInputRef.current.click();
          }
        };

        return (
          <div className="flex justify-center items-center space-x-2">
            {/* View Button - Always visible when file exists */}
            {document.file_path && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  handleViewDocument(
                    document.id,
                    document.file_name,
                    document.file_path
                  )
                }
                className="text-blue-600 border-blue-300 hover:bg-blue-50 hover:text-blue-800 flex items-center gap-1"
              >
                <Eye size={16} />
                View
              </Button>
            )}

            {/* Download Button - Visible for Ops and Sales teams when file exists */}
            {document.file_path && (roleId === 3 || roleId === 7) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  handleDownloadDocument(
                    document.id,
                    document.file_name,
                    document.file_path
                  )
                }
                className="text-green-600 border-green-300 hover:bg-green-50 hover:text-green-800 flex items-center gap-1"
              >
                <Download size={16} />
                Download
              </Button>
            )}

            {/* Upload/Update Button - Only for distributor in edit mode */}
            {isDistributor && !isReadOnly && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) =>
                    handleFileUpload(e, document.document_type, document.id)
                  }
                  disabled={
                    uploadingFile === document.document_type || !distributor_id
                  }
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={
                    uploadingFile === document.document_type || !distributor_id
                  }
                  onClick={handleUploadClick}
                  className={`flex items-center gap-1 ${document.file_path
                    ? "text-purple-600 border-purple-300 hover:bg-purple-50 hover:text-purple-800"
                    : "text-purple-600 border-purple-300 hover:bg-purple-50 hover:text-purple-800"
                    }`}
                >
                  <Upload size={16} />
                  {uploadingFile === document.document_type
                    ? "Uploading..."
                    : document.file_path
                      ? "Re-upload"
                      : "Upload"}
                </Button>
              </div>
            )}

            {/* Empty state for non-distributors when no file */}
            {!isDistributor && !document.file_path && (
              <span className="text-gray-400 text-sm">No document</span>
            )}
          </div>
        );
      },
    },
  ];



  const spocColumns = [
    ...(isReadOnly
      ? []
      : [
        {
          Header: "Actions",
          accessor: "actions",
          Cell: ({ row }) => (
            <div className="flex justify-center items-center space-x-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setEditingSpocId(row.original.spoc_id);
                  setForm((prev) => ({
                    ...prev,
                    spoc_name: row.original.spoc_name,
                    spoc_email1: row.original.spoc_email1,
                    spoc_email2: row.original.spoc_email2 || "",
                    spoc_mobile1: row.original.spoc_mobile1,
                    spoc_mobile2: row.original.spoc_mobile2 || "",
                    spoc_is_primary: row.original.spoc_is_primary === "Yes",
                  }));
                }}
                disabled={isReadOnly}
              >
                <SquarePen size={18} />
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => handleDeleteSPOC(row.original.spoc_id)}
                className="text-red-600"
                disabled={isReadOnly}
              >
                Delete
              </Button>
            </div>
          ),
          disableSortBy: true,
          size: 120,
        },
      ]),
    { Header: "SPOC Name", accessor: "spoc_name", size: 100 },
    { Header: "Email 1", accessor: "spoc_email1", size: 200 },
    { Header: "Email 2", accessor: "spoc_email2", size: 200 },
    { Header: "Mobile 1", accessor: "spoc_mobile1", size: 100 },
    { Header: "Mobile 2", accessor: "spoc_mobile2", size: 100 },
    {
      Header: "Is Primary",
      accessor: "spoc_is_primary",
      size: 80,
      Cell: ({ value }) => (
        <span
          className={
            value === "Yes" ? "text-green-600 font-semibold" : "text-gray-500"
          }
        >
          {value}
        </span>
      ),
    },
  ];

  const bankColumns = [
    { Header: "Bank Name", accessor: "bank_name", size: 150 },
    {
      Header: "Account Holder Name",
      accessor: "account_holder_name",
      size: 200,
    },
    { Header: "Account Number", accessor: "account_number", size: 150 },
    { Header: "IFSC Code", accessor: "ifsc_code", size: 150 },
    { Header: "Bank Branch", accessor: "bank_branch", size: 150 },
    ...(isReadOnly
      ? []
      : [
        {
          Header: "Action",
          accessor: "action",
          size: 100,
          Cell: ({ row }) => (
            <div className="flex justify-center items-center space-x-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setEditingBankId(row.original.id);
                  setForm((prev) => ({
                    ...prev,
                    bank_name: row.original.bank_name,
                    account_holder_name: row.original.account_holder_name,
                    account_number: row.original.account_number,
                    ifsc_code: row.original.ifsc_code,
                    bank_branch: row.original.bank_branch,
                  }));
                }}
                disabled={isReadOnly}
              >
                <SquarePen size={18} />
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => handleDeleteBank(row.original.id)}
                className="text-red-600"
                disabled={isReadOnly}
              >
                Delete
              </Button>
            </div>
          ),
        },
      ]),
  ];

  const renderInput = (
    field,
    label,
    placeholder,
    type = "text",
    cols = "col-span-1",
    icon,
    forceReadOnly = false
  ) => {
    const isMandatory = (field === "distributor_name" ||
      field === "pan_number" ||
      field === "pin_code" ||
      field === "city" ||
      field === "address" ||
      (!distributor_id &&
        (field === "spoc_name" ||
          field === "spoc_email1" ||
          field === "spoc_mobile1")));

    return (
      <div className={cols}>
        <InputField
          label={
            <span>
              {label}
              {isMandatory && <span className="text-red-500 ml-1">*</span>}
              {field === "arn_code" && isArnFromApi && (
                <span className="ml-1.5 normal-case font-medium text-indigo-400">(auto-fetched)</span>
              )}
            </span>
          }
          value={form[field]}
          onChange={(e) => {
            if (isReadOnly || forceReadOnly) return;
            let value = e.target.value;

            if (field === "pan_number") value = value.toUpperCase();
            if (field === "spoc_mobile1" || field === "spoc_mobile2") {
              value = value.replace(/\D/g, "").slice(0, 10);
            }
            if (field === "pin_code") {
              value = value.replace(/\D/g, "").slice(0, 6);
            }
            if (field === "city" || field === "spoc_name") {
              value = value.replace(/[^a-zA-Z\s]/g, "");
            }

            const fieldsNoSpecialChars = [
              "distributor_name", "city", "address", "spoc_name", "arn_code",
            ];
            if (fieldsNoSpecialChars.includes(field)) {
              value = value.replace(/[^\w\s]/gi, "");
            }

            handleChange(field, value);
          }}
          placeholder={placeholder}
          readOnly={isReadOnly || forceReadOnly}
          error={errors[field]}
          icon={icon}
        />
      </div>
    );
  };

  const removeSpecialCharacters = (value) => value.replace(/[^\w\s]/gi, "");





  const handleCancelEdit = () => {
    setEditingSpocId(null);
    setEditingBankId(null);
    setForm((prev) => ({
      ...prev,
      spoc_name: "",
      spoc_email1: "",
      spoc_email2: "",
      spoc_mobile1: "",
      spoc_mobile2: "",
      spoc_is_primary: false,
      bank_name: "",
      account_holder_name: "",
      account_number: "",
      ifsc_code: "",
      bank_branch: "",
    }));
  };

  const downloadPdf = () => {
    const contractId = localStorage.getItem("ops_contractId");

    if (!contractId) {
      alert("Contract ID missing. Please complete e-sign first.");
      return;
    }

    const url = `https://d14wm9j0bgcvlw.cloudfront.net/DistributorInvoiceFiles/Esign/signed_${contractId}.pdf`;

    const link = document.createElement("a");
    link.href = url;
    link.download = `signed_${contractId}.pdf`;
    link.click();
  };




  // Loading state
  if (loading) {
    return (
      <div className="max-w-screen-xl mx-auto p-4 space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading distributor details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8 animate-in fade-in duration-700">
      {/* Premium Header */}
      <div className="flex items-center gap-6">
        <button
          onClick={() => onBack && onBack()}
          className="w-10 h-10 rounded-full bg-white border border-gray-100 shadow-sm flex items-center justify-center hover:bg-gray-50 transition-all active:scale-95 group"
        >
          <ChevronLeft size={20} className="text-gray-400 group-hover:text-indigo-500 transition-colors" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800 tracking-tight">KYC Form</h1>
        </div>
      </div>

      {/* Distributor Details Section */}
      <FormSection title="Distributor Details" icon={Briefcase}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-8">
          {renderInput("distributor_name", "Distributor Name", "Enter Name", "text", "col-span-1", User)}
          {renderInput("arn_code", "APRN Code", "Enter APRN", "text", "col-span-1", Shield, isArnFromApi)}

          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider ml-1 flex items-center gap-1.5">
              <CalendarIcon size={12} className="text-indigo-400" /> APRN Expiry Date
              {form.arn_code && <span className="text-red-500">*</span>}
            </label>
            <div className="relative group">
              <PRCalendar
                value={form.arn_expiry_date}
                onChange={(e) => {
                  const date = e.value;
                  handleChange("arn_expiry_date", date);
                  if (date && new Date(date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)) {
                    toast.warn("APRN Expiry is in the past.");
                    setForm(prev => ({ ...prev, is_arn_active: false }));
                  } else {
                    setForm(prev => ({ ...prev, is_arn_active: true }));
                  }
                }}
                minDate={new Date()}
                placeholder="Select date"
                dateFormat="dd/mm/yy"
                showIcon
                disabled={isReadOnly}
                className={`w-full h-[42px] premium-calendar ${errors.arn_expiry_date ? "p-invalid" : ""}`}
                inputClassName="w-full h-[42px] px-4 rounded-lg bg-gray-50/50 border border-gray-100 outline-none text-sm font-semibold text-gray-700 focus:ring-2 focus:ring-indigo-100 focus:bg-white"
              />
              {errors.arn_expiry_date && <div className="absolute -bottom-5 left-1 text-[10px] font-bold text-red-500 uppercase">{errors.arn_expiry_date}</div>}
            </div>
          </div>

          {renderInput("pan_number", "PAN Number", "Enter PAN", "text", "col-span-1", CreditCard)}
          {renderInput("pin_code", "Pin Code", "Enter pin", "text", "col-span-1", MapPin)}
          {renderInput("city", "City", "Enter city", "text", "col-span-1", Globe)}
          {renderInput("address", "Address", "Enter address", "text", "col-span-2", MapPin)}

          <div className="flex items-center gap-3 pt-6">
            <div className="flex items-center h-5">
              <Checkbox
                checked={form.is_arn_active}
                onChange={toggleArnStatus}
                disabled={isArnExpired() || isReadOnly}
                className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
            </div>
            <div className="ml-2 text-xs">
              <label className="font-bold text-gray-600 uppercase tracking-tight">Is APRN Active</label>
              {isArnExpired() && <span className="text-red-500 ml-2 font-bold">(EXPIRED)</span>}
            </div>
          </div>
        </div>
      </FormSection>

      {/* SPOC Details Section */}
      <FormSection
        title="SPOC Details"
        icon={User}
        action={
          !isReadOnly && (
            <button
              onClick={editingSpocId ? handleUpdateSpoc : handleAddSpoc}
              disabled={!form.spoc_name?.trim() || !form.spoc_email1?.trim() || !form.spoc_mobile1?.trim()}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-black uppercase tracking-widest py-2 px-4 rounded-lg transition-all active:scale-95 disabled:opacity-50 disabled:grayscale"
            >
              <CirclePlus size={14} />
              {editingSpocId ? "Update SPOC" : "Add SPOC"}
            </button>
          )
        }
      >
        {!isReadOnly && !hasPrimarySpoc() && (
          <div className="mb-6 flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4 animate-pulse">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Info size={16} className="text-amber-600" />
            </div>
            <p className="text-xs font-bold text-amber-800 uppercase tracking-tight">
              At least one SPOC must be marked as primary before saving.
            </p>
          </div>
        )}

        {!isReadOnly && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8 pb-8 border-b border-gray-50">
            {renderInput("spoc_name", "SPOC Name", "Enter Name", "text", "col-span-1", User)}
            {renderInput("spoc_email1", "Email 1", "Enter email", "email", "col-span-1", Mail)}
            {renderInput("spoc_email2", "Email 2", "Enter email", "email", "col-span-1", Mail)}
            {renderInput("spoc_mobile1", "Mobile 1", "Enter mobile", "tel", "col-span-1", Phone)}
            {renderInput("spoc_mobile2", "Mobile 2", "Enter mobile", "tel", "col-span-1", Phone)}

            <div className="flex items-center min-h-[42px]">
              <FormControlLabel
                control={
                  <Checkbox
                    id="spoc_is_primary_checkbox"
                    checked={!!form.spoc_is_primary}
                    onChange={(e) => handleChange("spoc_is_primary", e.target.checked)}
                    className="text-indigo-600"
                  />
                }
                label={
                  <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider cursor-pointer select-none">
                    Is Primary <span className="text-red-500 ml-1">*</span>
                  </span>
                }
              />
            </div>
          </div>
        )}

        <div className="bg-gray-50/50 rounded-xl border border-gray-100 overflow-hidden">
          <ReusableTable columns={spocColumns} data={spocList} />
        </div>
      </FormSection>

      {/* Bank Details Section */}
      <FormSection
        title="Bank Details"
        icon={CreditCard}
        action={
          !isReadOnly && (
            <button
              onClick={editingBankId ? handleUpdateBank : handleAddBank}
              disabled={!form.bank_name?.trim() || !form.account_holder_name?.trim() || !form.account_number?.trim() || !form.ifsc_code?.trim() || !form.bank_branch?.trim()}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black uppercase tracking-widest py-2 px-4 rounded-lg transition-all active:scale-95 disabled:opacity-50 disabled:grayscale"
            >
              <CirclePlus size={14} />
              {editingBankId ? "Update Bank" : "Add Bank"}
            </button>
          )
        }
      >
        {!isReadOnly && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 pb-8 border-b border-gray-50">
            {renderInput("bank_name", "Bank Name", "Enter bank name", "text", "col-span-1", CreditCard)}
            {renderInput("account_holder_name", "Account Holder Name", "Enter name", "text", "col-span-1", User)}
            {renderInput("account_number", "Account Number", "Enter account number", "text", "col-span-1", CreditCard)}
            {renderInput("ifsc_code", "IFSC Code", "Enter IFSC", "text", "col-span-1", Shield)}
            {renderInput("bank_branch", "Bank Branch", "Enter branch", "text", "col-span-1", Briefcase)}
          </div>
        )}

        <div className="bg-gray-50/50 rounded-xl border border-gray-100 overflow-hidden">
          <ReusableTable columns={bankColumns} data={bankDetailsList} />
        </div>
      </FormSection>

      {/* Authorized Signatory Section */}
      <FormSection title="Authorized Signatory" icon={FileText}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {renderInput("authorized_signatory", "Authorized Signatory Name", "Enter authorized signatory name", "text", "col-span-1", User)}

        </div>
      </FormSection>


      {/* KYC Documents Section */}
      <FormSection
        title="KYC Documents"
        icon={FileText}
        action={
          (roleId === 3 || roleId === 7) && (kycDocuments.some(doc => doc.file_path) || (distributorData?.kyc_documents && distributorData.kyc_documents.some(doc => doc.file_path))) && (
            <button
              onClick={handleDownloadAllDocuments}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-black uppercase tracking-widest py-2 px-4 rounded-lg transition-all active:scale-95"
            >
              <Download size={14} />
              Download All
            </button>
          )
        }
      >
        {!distributor_id && !isReadOnly && (
          <div className="mb-6 flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
            <Info size={18} className="text-amber-600" />
            <p className="text-xs font-bold text-amber-800 uppercase tracking-tight">
              Please save distributor details before uploading KYC documents.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider ml-1">Document Type Profile</label>
            {isReadOnly ? (
              <div className="w-full h-[42px] px-4 rounded-lg bg-gray-100 border border-gray-200 flex items-center text-sm font-bold text-gray-600">
                {distributorData?.kyc_menu_types?.find(menu => menu.menu_id == selectedDocumentType)?.menu_name || "Not selected"}
              </div>
            ) : (
              <select
                value={selectedDocumentType}
                onChange={(e) => handleDocumentTypeChange(e.target.value)}
                className="w-full h-[42px] px-4 rounded-lg bg-gray-50/50 border border-gray-100 hover:border-indigo-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-sm font-bold text-gray-700 transition-all"
                disabled={!distributor_id}
              >
                <option value="">Select Document Type</option>
                {kycMenuTypes.map((menuType) => (
                  <option key={menuType.menu_id} value={menuType.menu_id}>{menuType.menu_name}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {selectedDocumentType && (
          <div className="bg-gray-50/50 rounded-xl border border-gray-100 overflow-hidden">
            {isReadOnly ? (
              distributorData?.kyc_documents?.length > 0 ? (
                <ReusableTable
                  columns={kycColumns}
                  data={distributorData.kyc_documents.map(doc => ({
                    id: doc.document_id,
                    document_type: doc.document_name,
                    file_name: doc.file_path ? doc.file_path.split('/').pop() : null,
                    uploaded_date: doc.uploaded_on,
                    file_path: doc.file_path,
                    is_mandatory: doc.is_mandetory,
                    menu_id: doc.menu_id,
                    upload_id: doc.upload_id,
                  }))}
                />
              ) : (
                <div className="py-12 flex flex-col items-center justify-center text-gray-400 opacity-60">
                  <FileText size={48} strokeWidth={1} className="mb-2" />
                  <p className="text-[10px] font-black uppercase tracking-widest">No KYC documents found</p>
                </div>
              )
            ) : (
              kycDocuments.length > 0 ? (
                <ReusableTable columns={kycColumns} data={kycDocuments} />
              ) : (
                <div className="py-12 flex flex-col items-center justify-center text-gray-400 opacity-60">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-4" />
                  <p className="text-[10px] font-black uppercase tracking-widest tracking-[0.2em]">Synchronizing templates...</p>
                </div>
              )
            )}
          </div>
        )}
      </FormSection>

      {/* Footer Actions Section */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 flex flex-col md:flex-row items-center justify-between gap-6 transition-all animate-in slide-in-from-bottom-8 duration-1000">
        <div className="flex items-center gap-4">
          {distributorData?.onboarding_detail_status === 5 && (
            <div className="flex items-center gap-3 bg-indigo-50 px-4 py-2 rounded-lg border border-indigo-100">
              <span className="text-[11px] font-black text-indigo-500 uppercase">Agreement Ready</span>
              <button
                onClick={downloadPdf}
                className="flex items-center gap-2 text-indigo-700 hover:text-indigo-900 font-bold text-sm"
              >
                <Download size={16} />
                Download Signed Copy
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          {propRole === 2 && !isReadOnly && (
            <>
              <button
                onClick={handleSaveDraft}
                disabled={loading}
                className="px-8 h-[42px] rounded-lg bg-white border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50 transition-all active:scale-95 disabled:opacity-50"
              >
                Save As Draft
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-10 h-[42px] rounded-lg bg-indigo-600 text-white font-bold text-sm shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? <CirclePlus className="animate-spin" size={18} /> : <CheckCircle2 size={18} />}
                {loading ? "Submitting..." : "Submit Registry"}
              </button>
            </>
          )}

          {propRole === 3 && (
            <>
              <button
                onClick={handleFormAmcReject}
                className="px-8 h-[42px] rounded-lg bg-white border border-red-200 text-red-600 font-bold text-sm hover:bg-red-50 transition-all active:scale-95 mb-0"
              >
                Reject Onboarding
              </button>
              <button
                onClick={handleFormAmcApprove}
                className="px-10 h-[42px] rounded-lg bg-indigo-600 text-white font-bold text-sm shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 flex items-center gap-2"
              >
                <CheckCircle2 size={18} />
                Approve & E-sign
              </button>
            </>
          )}

          {propRole === 7 && (
            <>
              <button
                onClick={async () => {
                  if (!distributor_id) { toast.error("Missing distributor id"); return; }
                  try {
                    const resp = await post("/onboarding/sales/reject", { distributor_sequence_number: distributor_id });
                    if (resp.data?.Success) { toast.success(resp.data.Message || "Rejected"); if (onBack) onBack(); }
                    else { toast.error(resp.data?.Message || "Rejection failed"); }
                  } catch { toast.error("Error rejecting"); }
                }}
                className="px-8 h-[42px] rounded-lg bg-white border border-red-200 text-red-600 font-bold text-sm hover:bg-red-50 transition-all active:scale-95"
              >
                Reject
              </button>
              <button
                onClick={async () => {
                  try {
                    const resp = await post("/onboarding/sales/approve", { distributor_sequence_number: distributor_id });
                    if (resp.data.Success) { toast.success("Approved"); if (onBack) onBack(); }
                    else { toast.error(resp.data.Message || "Approval failed"); }
                  } catch { toast.error("Error approving"); }
                }}
                className="px-10 h-[42px] rounded-lg bg-indigo-600 text-white font-bold text-sm shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 flex items-center gap-2"
              >
                <CheckCircle2 size={18} />
                Approve Entry
              </button>
            </>
          )}
        </div>
      </div>

      {/* E-sign Modal */}
      <CustomDialog
        visible={esignModalVisible}
        onHide={() => {
          setEsignModalVisible(false);
          setInPresenceOf("");
        }}
        header="E-sign Approval"
        width="50vw"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              In presenece of
            </label>
            <input
              type="text"
              value={inPresenceOf}
              onChange={(e) => setInPresenceOf(e.target.value)}
              placeholder="Enter your full name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setEsignModalVisible(false);
                setInPresenceOf("");
              }}
              disabled={esignLoading}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleEsignSubmit}
              disabled={esignLoading}
            >
              {esignLoading ? "Submitting..." : "Submit E-sign"}
            </Button>
          </div>
        </div>
      </CustomDialog>

      {/* Rejection Remark Modal */}
      <CustomDialog
        visible={rejectionModalVisible}
        onHide={() => {
          setRejectionModalVisible(false);
          setRejectionRemark("");
          setRejectionType("");
          setPendingRejection(false);
        }}
        header={`Reject Distributor - ${rejectionType === "sales" ? "Sales" : "AMC"
          }`}
        width="50vw"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rejection Remark *
            </label>
            <textarea
              value={rejectionRemark}
              onChange={(e) => setRejectionRemark(e.target.value)}
              placeholder="Please provide reason for rejection..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={4}
            />
            {!rejectionRemark && pendingRejection && (
              <p className="text-red-500 text-sm mt-1">
                Rejection remark is required
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setRejectionModalVisible(false);
                setRejectionRemark("");
                setRejectionType("");
                setPendingRejection(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={() => {
                if (!rejectionRemark.trim()) {
                  setPendingRejection(true);
                  toast.error("Please provide rejection remark");
                  return;
                }

                if (rejectionType === "sales") {
                  handleSalesRejectConfirm();
                } else if (rejectionType === "amc") {
                  handleAmcReject();
                }
              }}
            >
              Confirm Reject
            </Button>
          </div>
        </div>
      </CustomDialog>
      <ToastContainer position="top-right" autoClose={3000} />
    </div >
  );
};

export default AddDistributorForm;